
import { getApiBase, isPlatformHost } from "../../helpers/apiBase";/**
 * SSE consumer for sf-chat-service conversational survey endpoints.
 * Adapted from the Eva admin streaming helper — same fetch + ReadableStream
 * pattern because EventSource cannot send Authorization headers.
 */

export class StreamPreFlightError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "StreamPreFlightError";
    if (typeof options.status === "number") this.status = options.status;
    if (options.cause !== undefined) this.cause = options.cause;
  }
}

function splitSseFrames(buffer) {
  const events = [];
  let rest = buffer;
  let idx = rest.indexOf("\n\n");
  while (idx !== -1) {
    const frame = rest.slice(0, idx);
    rest = rest.slice(idx + 2);
    const dataLines = [];
    frame.split("\n").forEach((line) => {
      if (!line || line.startsWith(":")) return;
      if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).replace(/^ /, ""));
      }
    });
    if (dataLines.length > 0) events.push(dataLines.join("\n"));
    idx = rest.indexOf("\n\n");
  }
  return { events, rest };
}

/**
 * @param {object} args
 * @param {string} args.url
 * @param {string} args.authToken  Full Authorization value (e.g. "jwt <token>")
 * @param {object} args.body
 * @param {AbortSignal} [args.signal]
 */
export async function* streamSurveyChat({ url, authToken, body, signal }) {
  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      mode: "cors",
      headers: {
        Authorization: authToken,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err && err.name === "AbortError") throw err;
    throw new StreamPreFlightError(
      `Survey streaming request failed: ${err?.message || err}`,
      { cause: err }
    );
  }

  if (!response.ok) {
    throw new StreamPreFlightError(
      `Survey streaming request returned HTTP ${response.status}`,
      { status: response.status }
    );
  }
  if (!response.body || typeof response.body.getReader !== "function") {
    throw new StreamPreFlightError(
      "Survey streaming response body is not readable in this environment"
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    let reading = true;
    while (reading) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) {
        reading = false;
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = splitSseFrames(buffer);
      buffer = rest;
      for (let i = 0; i < events.length; i += 1) {
        let parsed;
        try {
          parsed = JSON.parse(events[i]);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[survey-stream] skipped malformed SSE frame", err);
          // eslint-disable-next-line no-continue
          continue;
        }
        yield parsed;
        if (parsed?.type === "done" || parsed?.type === "error") {
          reading = false;
          break;
        }
      }
    }

    buffer += decoder.decode();
    const { events: tail } = splitSseFrames(`${buffer}\n\n`);
    for (let i = 0; i < tail.length; i += 1) {
      try {
        yield JSON.parse(tail[i]);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[survey-stream] skipped malformed trailing SSE frame", err);
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch (_e) {
      // ignore
    }
  }
}

/**
 * Resolve chat-service base URL for survey streaming.
 *
 * On a customer's own survey domain, always use the same-origin "/api" base
 * (getApiBase). CloudFront strips the prefix and forwards to the API host,
 * where the load balancer sends /ai/* to the chat service. Calling the API
 * host directly from a customer domain is cross-origin, and the chat service
 * does not allow customer domains in CORS, so the browser would block it.
 *
 * On our own hosts: prefer the URL returned by sf-api bootstrap, then
 * localhost in dev, then REACT_APP_CHAT_SERVICE_URL, then the API base.
 */
export function resolveChatServiceBase(chatServiceUrl, host) {
  if (!isPlatformHost(host ?? (typeof window !== "undefined" ? window.location.host : ""))) {
    return getApiBase(host);
  }
  if (chatServiceUrl) return chatServiceUrl.replace(/\/$/, "");
  if (process.env.NODE_ENV === "development") return "http://localhost:7007";
  return process.env.REACT_APP_CHAT_SERVICE_URL || getApiBase(host);
}

// Every Eva endpoint is under /ai (SF-4590). The load balancer sends /ai/* to
// the chat service, and on customer survey domains /api/ai/* reaches it the same
// way, through the existing /api prefix strip.
export function getSurveyStreamUrl(chatServiceUrl, host) {
  return `${resolveChatServiceBase(chatServiceUrl, host)}/ai/survey/message/stream`;
}

export function getSurveySessionUrl(chatServiceUrl, host) {
  return `${resolveChatServiceBase(chatServiceUrl, host)}/ai/survey/session`;
}
